/**
 * Normalizes the `errors` field of an `ApiResponse` into a `string[]`.
 *
 * The type in `src/types/commons.ts` says `string[]`, and most endpoints honour
 * that. The filing-session upload route does NOT: its `413` bodies send a bare
 * string —
 *
 *   { "status": false, "statusCode": 413, "errors": "File exceeds the 50 MB ..." }
 *
 * Two bugs lived in that gap before this util existed:
 *
 *   - `FormSubmitService.createErrorHandler` tested `errors.length > 0`, which is
 *     truthy for a non-empty string, so it returned the raw body and shadowed its
 *     own purpose-built 413 branch (making that branch dead code).
 *   - `FilingSessionUploader.emitError` tested `Array.isArray(errors)`, which is
 *     false for a string, so it fell through to the generic "Please try again
 *     later." message and threw the server's real explanation away.
 *
 * Route every read of `.errors` through here. Never test it with `.length` or
 * `Array.isArray` at a call site.
 *
 * Pure and DI-free by contract — imports nothing.
 */
export function normalizeApiErrors(errors: unknown): string[] {
  const raw: unknown[] =
    typeof errors === 'string' ? [errors] : Array.isArray(errors) ? errors : [];

  return raw
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}
